import {
  collectPlaybookSections,
  type PlaybookCriterionNode,
  type PlaybookNode,
  type PlaybookSectionNode,
  type PlaybookTree,
} from "@repo/app-types/entities/playbook";
import { assertUnreachable } from "@repo/app-types/helpers/utilities";
import { IconChevronRight, IconFolder } from "@tabler/icons-react";
import { useMemo, type FC, type ReactNode } from "react";
import {
  Button as AriaButton,
  Tree,
  TreeItem,
  TreeItemContent,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { GradingContextInfoTrigger } from "../../components/assessment/gradingContextInfoTrigger.js";

export interface PlaybookCriteriaTreeTableProps {
  readonly assessmentTree: PlaybookTree;
  readonly selectedCriterionId?: string;
  /** When false, the Time column header and cells are not rendered. */
  readonly showsTimeColumn?: boolean;
  /**
   * Tightens the horizontal cell padding for narrow containers, e.g. the
   * collapsed left panel of the playbook page once a criterion is selected.
   */
  readonly isCompact?: boolean;
  readonly renderCriterion: (criterion: PlaybookCriterionNode) => {
    /** Optional content rendered after the criterion name (e.g. impact pill). */
    titleSuffix?: ReactNode;
    score: ReactNode;
    time?: ReactNode;
    onPress?: () => void;
  };
  readonly renderSection: (section: PlaybookSectionNode) => {
    score: ReactNode;
    time?: ReactNode;
  };
}

/**
 * Shared chrome for the playbook criteria table: a 3-column grid (Criteria,
 * Score, Time) with a tree of sections and criteria. Pure presentation —
 * cell content is provided by the caller via render props so the same
 * layout can be driven by teammate-level or playbook-level data sources.
 */
export const PlaybookCriteriaTreeTable: FC<PlaybookCriteriaTreeTableProps> = ({
  assessmentTree,
  selectedCriterionId,
  showsTimeColumn = false,
  isCompact = false,
  renderCriterion,
  renderSection,
}) => {
  const sectionNodeIds = useMemo(
    () => collectPlaybookSections(assessmentTree).map((section) => section.id),
    [assessmentTree],
  );

  // Horizontal padding shared by the header and every cell, so the columns
  // stay aligned. Compact mode drops the wide `sm` gutter.
  const cellPaddingClassName = isCompact ? "px-2" : "px-3 sm:px-6";

  // Indentation per tree level. Shifted one level back for criteria so a
  // criterion lines up with its parent section — e.g. a level=2 criterion
  // sits at the same x as a level=1 section (base padding, no extra indent).
  const sectionIndentClassName = [
    "group-aria-[level=2]:pl-8 sm:group-aria-[level=2]:pl-12",
    "group-aria-[level=3]:pl-12 sm:group-aria-[level=3]:pl-20",
  ].join(" ");
  const criterionIndentClassName = [
    "group-aria-[level=3]:pl-8 sm:group-aria-[level=3]:pl-12",
    "group-aria-[level=4]:pl-12 sm:group-aria-[level=4]:pl-20",
  ].join(" ");

  const renderNode = (node: PlaybookNode): ReactNode => {
    switch (node.type) {
      case "section": {
        const { score, time } = renderSection(node);
        const tooltipContent = node.description;
        return (
          <TreeItem
            key={node.id}
            id={node.id}
            textValue={node.name || "Section"}
            className="group col-span-full grid grid-cols-subgrid items-stretch border-b border-border-light last:border-b-0"
          >
            <TreeItemContent>
              <div
                className={twMerge(
                  "flex min-w-0 items-center gap-2 py-2 font-semibold",
                  cellPaddingClassName,
                  sectionIndentClassName,
                )}
              >
                <AriaButton
                  slot="chevron"
                  className="shrink-0 text-muted-foreground hover:text-gray-700"
                  aria-label="Toggle"
                >
                  <IconChevronRight className="size-4 transition-transform group-aria-expanded:rotate-90" />
                </AriaButton>
                <IconFolder
                  className="size-4 shrink-0 text-muted-foreground"
                  aria-hidden="true"
                />
                <span className="flex min-w-0 items-center gap-1">
                  <span className="truncate">{node.name}</span>
                  {tooltipContent && (
                    <GradingContextInfoTrigger
                      title={node.name}
                      content={tooltipContent}
                    />
                  )}
                </span>
              </div>
              <div
                className={twMerge(
                  "flex items-center justify-start py-2",
                  cellPaddingClassName,
                )}
              >
                {score}
              </div>
              {showsTimeColumn && (
                <div
                  className={twMerge(
                    "flex items-center justify-end py-2",
                    cellPaddingClassName,
                  )}
                >
                  {time}
                </div>
              )}
            </TreeItemContent>
            {node.nodes.map(renderNode)}
          </TreeItem>
        );
      }
      case "criteria": {
        const { titleSuffix, score, time, onPress } = renderCriterion(node);
        const tooltipContent = node.grading_context || node.description;
        const isSelected = node.id === selectedCriterionId;
        return (
          <TreeItem
            key={node.id}
            id={node.id}
            textValue={node.name || "Criteria"}
            className={twMerge(
              "group col-span-full grid grid-cols-subgrid items-stretch border-b border-border-light last:border-b-0",
              onPress && "cursor-pointer hover:bg-muted/50",
              isSelected && "bg-muted",
            )}
            onAction={onPress}
          >
            <TreeItemContent>
              <div
                className={twMerge(
                  "flex min-w-0 items-center gap-2 py-2",
                  cellPaddingClassName,
                  criterionIndentClassName,
                )}
              >
                <span className="size-4 shrink-0" aria-hidden="true" />
                <span className="flex min-w-0 items-center gap-1">
                  <span className="truncate">{node.name}</span>
                  {tooltipContent && (
                    <GradingContextInfoTrigger
                      title={node.name}
                      content={tooltipContent}
                    />
                  )}
                </span>
                {titleSuffix}
              </div>
              <div
                className={twMerge(
                  "flex items-center justify-start py-2",
                  cellPaddingClassName,
                )}
              >
                {score}
              </div>
              {showsTimeColumn && (
                <div
                  className={twMerge(
                    "flex items-center justify-end py-2",
                    cellPaddingClassName,
                  )}
                >
                  {time}
                </div>
              )}
            </TreeItemContent>
          </TreeItem>
        );
      }
      default: {
        assertUnreachable(node);
      }
    }
  };

  return (
    <div
      className={twMerge(
        "grid overflow-hidden rounded-lg border border-border-light bg-white",
        showsTimeColumn
          ? "grid-cols-[minmax(0,1fr)_auto_auto]"
          : "grid-cols-[minmax(0,1fr)_auto]",
      )}
    >
      <div className="col-span-full grid grid-cols-subgrid items-center border-b border-border-light py-3 text-xs font-semibold text-foreground">
        <div className={cellPaddingClassName}>Criteria</div>
        {/* Left-aligned, like the cells below it: score pills all share a
            width, so a left-aligned header lines up with their left edge
            rather than with the ragged trend-delta column beside them. */}
        <div className={twMerge("text-left", cellPaddingClassName)}>Score</div>
        {/* Right-aligned, unlike Score: a time cell is a right-aligned
            duration followed by a bar that ends at the cell's right edge, so
            that edge is what the header lines up with. */}
        {showsTimeColumn && (
          <div className={twMerge("text-right", cellPaddingClassName)}>
            Time
          </div>
        )}
      </div>

      <Tree
        aria-label="Playbook criteria"
        className="col-span-full grid grid-flow-row grid-cols-subgrid text-sm"
        defaultExpandedKeys={sectionNodeIds}
      >
        {assessmentTree.nodes.map(renderNode)}
      </Tree>
    </div>
  );
};
